import { Action } from '@ngrx/store';
import { Mailbox, MailboxData } from '../../models/mailboxes.model';
import { TechnicalError } from '../../models/errors.model';
import { type } from '../util';

/**
 * For each action type in an action group, make a simple
 * enum object for all of this group's action types.
 *
 * The 'type' utility function coerces strings into string
 * literal types and runs a simple check to guarantee all
 * action types in the application are unique.
 */
export const ActionTypes = {
  LOAD: type('[Mailbox] Load'),
  LOAD_FAIL: type('[Mailbox] Load Fail'),
  LOADED: type('[Mailbox] Loaded'),
  SELECT: type('[Mailbox] Select'),
  CREATE: type('[Mailbox] Create'),
  CREATE_SUCCESS: type('[Mailbox] Create Success'),
  CREATE_FAIL: type('[Mailbox] Create Fail'),
  RESET_SUCCESS: type('[Mailbox] Reset Success'),
  RESET_ERROR: type('[Mailbox] Reset Error'),
  ACTIVATE: type('[Mailbox] Activate'),
  OPEN: type('[Mailbox] OpenMailboxAction'),
  VALIDATE: type('[Mailbox] ValidateEmailAction'),
  VALIDATE_SUCCESS: type('[Mailbox] ValidateSuccessEmailAction'),
  VALIDATE_FAILED: type('[Mailbox] ValidateFailedEmailAction'),
};

/**
 * Every action is comprised of at least a type and an optional
 * payload. Expressing actions as classes enables powerful
 * type checking in reducer functions.
 *
 * See Discriminated Unions: https://www.typescriptlang.org/docs/handbook/advanced-types.html#discriminated-unions
 */

export class LoadAction implements Action {
  type = ActionTypes.LOAD;

  // This constructor is added just make the type have a payload
  constructor(public payload?: any) {}
}

export class LoadFailAction implements Action {
  type = ActionTypes.LOAD_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class LoadedAction implements Action {
  type = ActionTypes.LOADED;

  constructor(public payload: MailboxData) {}
}

export class SelectAction implements Action {
  type = ActionTypes.SELECT;

  constructor(public payload: Mailbox) {}
}

export class CreateAction implements Action {
  type = ActionTypes.CREATE;

  constructor(public payload: { aliasId: string; password: string }) {}
}

export class CreateSuccessAction implements Action {
  type = ActionTypes.CREATE_SUCCESS;

  constructor(public payload: Mailbox) {}
}

export class CreateFailAction implements Action {
  type = ActionTypes.CREATE_FAIL;

  constructor(public payload: TechnicalError) {}
}

export class ResetSuccess implements Action {
  type = ActionTypes.RESET_SUCCESS;
  payload = null;
}

export class ResetError implements Action {
  type = ActionTypes.RESET_ERROR;
  payload = null;
}

export class ActivateAction implements Action {
  type = ActionTypes.ACTIVATE;

  constructor(public payload: Mailbox) {}
}

export class OpenMailboxAction implements Action {
  type = ActionTypes.OPEN;

  constructor(public payload: any) {}
}

export class ValidateEmailAction implements Action {
  type = ActionTypes.VALIDATE;

  constructor(public payload: string) {}
}

export class ValidateEmailSuccessAction implements Action {
  type = ActionTypes.VALIDATE_SUCCESS;

  constructor(public payload: any) {}
}

export class ValidateEmailFailedAction implements Action {
  type = ActionTypes.VALIDATE_FAILED;

  constructor(public payload: any) {}
}

/**
 * Export a type alias of all actions in this action group
 * so that reducers can easily compose action types
 */
export type Actions =
  | LoadAction
  | LoadFailAction
  | LoadedAction
  | SelectAction
  | CreateAction
  | CreateSuccessAction
  | CreateFailAction
  | ResetError
  | ResetSuccess
  | ActivateAction
  | OpenMailboxAction
  | ValidateEmailAction
  | ValidateEmailSuccessAction
  | ValidateEmailFailedAction;
